import React, { useCallback, useRef, useState } from 'react';
import { Upload, FolderUp, FilePlus, FileArchive, X } from 'lucide-react';

/**
 * Universal upload dropzone — supports:
 *  • Drag & drop (files or whole folders via DataTransferItem.webkitGetAsEntry).
 *  • Manual file picker (CSV / Excel / PDF / ZIP).
 *  • Folder picker (chrome/edge — webkitdirectory).
 *  • Multiple files with chip preview + remove.
 *
 * Props:
 *  - value: File[]
 *  - onChange: (files: File[]) => void
 *  - disabled: bool
 */
export default function DropZone({ value = [], onChange, disabled }) {
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const [over, setOver] = useState(false);

  const _appendFiles = useCallback((newFiles) => {
    const seen = new Set(value.map((f) => `${f.name}::${f.size}`));
    const merged = [...value];
    for (const f of newFiles) {
      const k = `${f.name}::${f.size}`;
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(f);
      }
    }
    onChange(merged);
  }, [value, onChange]);

  // Recursively walk a directory entry collected via dragdrop
  const _walkEntry = (entry, path = '', collector = []) => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((f) => {
          // Preserve the relative path inside the dropped folder by
          // overriding the displayed name. Backend just uses .name.
          collector.push(f);
          resolve();
        });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        reader.readEntries(async (entries) => {
          await Promise.all(entries.map((e) =>
            _walkEntry(e, `${path}${entry.name}/`, collector)));
          resolve();
        });
      } else { resolve(); }
    });
  };

  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    if (disabled) return;
    const dt = e.dataTransfer;
    const collected = [];
    if (dt.items && dt.items.length) {
      const entries = [];
      for (const item of dt.items) {
        // eslint-disable-next-line no-unused-expressions
        item?.webkitGetAsEntry && entries.push(item.webkitGetAsEntry());
      }
      const valid = entries.filter(Boolean);
      if (valid.length) {
        await Promise.all(valid.map((en) => _walkEntry(en, '', collected)));
      } else {
        for (const f of dt.files) collected.push(f);
      }
    } else {
      for (const f of dt.files) collected.push(f);
    }
    _appendFiles(collected);
  }, [_appendFiles, disabled]);

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    _appendFiles(list);
    e.target.value = '';
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  const totalMB = (value.reduce((s, f) => s + (f.size || 0), 0) / (1024 * 1024)).toFixed(2);

  return (
    <div className="space-y-3">
      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed transition-all p-8 text-center ${
          over ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-300 bg-slate-50/40'
        } ${disabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className={`w-12 h-12 mx-auto mb-3 ${over ? 'text-indigo-600' : 'text-slate-400'}`} />
        <div className="text-lg font-semibold text-slate-800 mb-1">
          أَفلِت ملَفات الكُشوف البَنكية هُنا
        </div>
        <div className="text-sm text-slate-500 mb-4">
          أو اِنقُر لاختيار ملَف · يَدعَم CSV / Excel / PDF / ZIP — حَتّى مُجَلَّد كامِل بالـ Drag
        </div>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                  data-testid="pick-files-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
                  disabled={disabled}>
            <FilePlus className="w-4 h-4" /> اختيار ملَفات
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); folderRef.current?.click(); }}
                  data-testid="pick-folder-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2 disabled:opacity-50"
                  disabled={disabled}>
            <FolderUp className="w-4 h-4" /> اختيار مُجَلَّد
          </button>
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-3 py-2">
            <FileArchive className="w-4 h-4" /> ZIP مَدعوم
          </span>
        </div>
        <input ref={inputRef} type="file" multiple
               accept=".csv,.xlsx,.xlsm,.pdf,.zip"
               onChange={onPick} className="hidden" />
        <input ref={folderRef} type="file" multiple
               webkitdirectory="true" directory=""
               onChange={onPick} className="hidden" />
      </div>

      {value.length > 0 && (
        <div className="rounded-xl border bg-white p-3" data-testid="dropzone-files">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700">
              {value.length} ملَف · {totalMB} MB
            </div>
            <button onClick={() => onChange([])}
                    data-testid="clear-files-btn"
                    className="text-xs text-red-600 hover:text-red-800">
              مَسح الكُل
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {value.map((f, i) => (
              <span key={i} data-testid={`file-chip-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-800 text-xs px-3 py-1">
                <span className="font-mono">{f.name}</span>
                <span className="text-[10px] text-slate-500">
                  {(f.size / 1024).toFixed(1)} KB
                </span>
                <button onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                        className="text-slate-400 hover:text-red-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
